import {
  BLACK,
  COLOR_MASK,
  type Color,
  FLAG_ACTION,
  FLAG_CAPTURE,
  FLAG_CASTLE,
  FLAG_DROP,
  TYPE_MASK,
  WHITE,
  isNeutral,
  makePiece,
  moveFlags,
  moveFrom,
  movePromo,
  moveTo,
  parseSquare,
  pieceColor,
  squareName,
} from './board.ts';
import { PIECE_DEFS, baseType, pieceDef, typeForLetter } from './pieces.ts';
import type { Rules } from './rules.ts';
import type { Position } from './types.ts';

// FEN: the standard six fields, then any fields the variant defines (SnailChess adds snail
// squares and move counts). Two extensions to the board field:
//   - neutral pieces are written `*` + letter (a duck on e4 is `*d`),
//   - pieces in hand follow in brackets, as in crazyhouse: `…/RNBQKBNR[Qn]`.

export class FenError extends Error {}

export function pieceLetter(p: number): string {
  const l = pieceDef(p & TYPE_MASK).letter;
  if (isNeutral(p)) return `*${l}`;
  return pieceColor(p) === WHITE ? l.toUpperCase() : l;
}

export function parseFen(rules: Rules, fen: string): Position {
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 4) throw new FenError('FEN needs at least 4 fields');
  const [boardField, turn, castling, ep, half = '0', full = '1', ...extra] = parts;
  const board = new Int8Array(64);
  const royals: [number, number] = [-1, -1];
  let hands: Int8Array | null = rules.drops ? new Int8Array(64) : null;

  let placement = boardField;
  const bracket = boardField.indexOf('[');
  if (bracket >= 0) {
    if (!boardField.endsWith(']')) throw new FenError('Unclosed pieces-in-hand field');
    placement = boardField.slice(0, bracket);
    hands ??= new Int8Array(64);
    for (const ch of boardField.slice(bracket + 1, -1)) {
      if (ch === '-') continue;
      const t = typeForLetter(ch);
      if (!t || !rules.defs[t]) throw new FenError(`Bad piece in hand "${ch}"`);
      const c: Color = ch === ch.toUpperCase() ? WHITE : BLACK;
      hands[c * 32 + t]++;
    }
  }

  const rows = placement.split('/');
  if (rows.length !== 8) throw new FenError('FEN board needs 8 ranks');
  for (let i = 0; i < 8; i++) {
    const rank = 7 - i;
    let file = 0;
    const row = rows[i];
    for (let j = 0; j < row.length; j++) {
      const ch = row[j];
      if (ch >= '1' && ch <= '8') {
        file += Number(ch);
        continue;
      }
      let neutral = false;
      let letter = ch;
      if (ch === '*') {
        neutral = true;
        letter = row[++j] ?? '';
      }
      const t = typeForLetter(letter);
      if (!t || !rules.defs[t] || file > 7) throw new FenError(`Bad FEN piece "${letter}"`);
      const sq = rank * 8 + file;
      if (neutral) board[sq] = t;
      else {
        const c: Color = letter === letter.toUpperCase() ? WHITE : BLACK;
        board[sq] = makePiece(c, t);
        if (rules.isRoyal[t]) {
          if (royals[c] >= 0) throw new FenError('More than one king per side');
          royals[c] = sq;
        }
      }
      file++;
    }
    if (file !== 8) throw new FenError('FEN rank has wrong length');
  }
  // Variants that crown heirs say who's royal in their own FEN fields.
  const crowns = rules.variant.loseWithoutRoyal === false;
  if (rules.hasRoyal && !crowns && (royals[0] < 0 || royals[1] < 0)) throw new FenError('Each side needs a king');
  if (turn !== 'w' && turn !== 'b') throw new FenError('Bad side to move');

  let rights = 0;
  if (castling !== '-') {
    for (const ch of castling) {
      const bit = 'KQkq'.indexOf(ch);
      if (bit < 0) throw new FenError('Bad castling field');
      rights |= 1 << bit;
    }
  }
  const epSq = ep === '-' ? -1 : parseSquare(ep);
  if (ep !== '-' && epSq < 0) throw new FenError('Bad en passant square');

  const pos: Position = {
    board,
    turn: turn === 'w' ? WHITE : BLACK,
    castling: rights,
    ep: epSq,
    halfmove: Math.max(0, Number(half) || 0),
    fullmove: Math.max(1, Number(full) || 1),
    royals,
    hands,
    ext: rules.extSize ? new Int32Array(rules.extSize) : null,
  };
  rules.variant.parseExtra?.(extra, pos);
  return pos;
}

export function toFen(rules: Rules, pos: Position): string {
  const skip = rules.variant.fenSkip;
  const rows: string[] = [];
  for (let rank = 7; rank >= 0; rank--) {
    let row = '';
    let empty = 0;
    for (let file = 0; file < 8; file++) {
      const p = pos.board[rank * 8 + file];
      if (p === 0 || (skip && skip.includes(p & TYPE_MASK))) {
        empty++;
        continue;
      }
      if (empty) row += empty;
      empty = 0;
      row += pieceLetter(p);
    }
    if (empty) row += empty;
    rows.push(row);
  }
  let boardField = rows.join('/');
  if (rules.drops && pos.hands) {
    let hand = '';
    for (const c of [WHITE, BLACK] as Color[]) {
      for (const t of rules.types) {
        const n = pos.hands[c * 32 + t];
        const l = pieceDef(t).letter;
        hand += (c === WHITE ? l.toUpperCase() : l).repeat(Math.max(0, n));
      }
    }
    boardField += `[${hand}]`;
  }
  let castling = '';
  'KQkq'.split('').forEach((ch, i) => {
    if (pos.castling & (1 << i)) castling += ch;
  });
  return [
    boardField,
    pos.turn === WHITE ? 'w' : 'b',
    castling || '-',
    pos.ep >= 0 ? squareName(pos.ep) : '-',
    pos.halfmove,
    pos.fullmove,
    ...(rules.variant.formatExtra?.(pos) ?? []),
  ].join(' ');
}

/** Squares of an action move (any 0–63 value) as text; the payload in base 36. */
function actionToUci(m: number): string {
  const payload = movePromo(m);
  return `!${squareName(moveFrom(m))}${squareName(moveTo(m))}${payload ? payload.toString(36) : ''}`;
}

export function moveToUci(m: number): string {
  if (moveFlags(m) & FLAG_ACTION) return actionToUci(m);
  if (moveFlags(m) & FLAG_DROP) return `${pieceDef(moveFrom(m)).letter.toUpperCase()}@${squareName(moveTo(m))}`;
  const promo = movePromo(m);
  return squareName(moveFrom(m)) + squareName(moveTo(m)) + (promo ? pieceDef(promo).letter : '');
}

/** Finds the legal move matching a UCI string like "e2e4", "e7e8q" or "N@f3". */
export function uciToMove(rules: Rules, pos: Position, uci: string, legal = rules.legalMoves(pos)): number | null {
  if (typeof uci !== 'string' || uci.length > 8) return null;
  if (uci[0] === '!') {
    for (const m of legal) if (moveFlags(m) & FLAG_ACTION && actionToUci(m) === uci) return m;
    return null;
  }
  if (uci[1] === '@') {
    const t = typeForLetter(uci[0]);
    const to = parseSquare(uci.slice(2, 4));
    if (!t || to < 0) return null;
    for (const m of legal) if (moveFlags(m) & FLAG_DROP && moveFrom(m) === t && moveTo(m) === to) return m;
    return null;
  }
  const from = parseSquare(uci.slice(0, 2));
  const to = parseSquare(uci.slice(2, 4));
  const promoChar = uci.slice(4, 5).toLowerCase();
  const promo = promoChar ? typeForLetter(promoChar) : 0;
  if (from < 0 || to < 0 || (promoChar && !promo)) return null;
  for (const m of legal) {
    if (moveFlags(m) & FLAG_DROP) continue;
    if (moveFrom(m) === from && moveTo(m) === to && movePromo(m) === promo) return m;
  }
  return null;
}

export function moveToSan(rules: Rules, pos: Position, m: number, legal = rules.legalMoves(pos)): string {
  const to = moveTo(m);
  const flags = moveFlags(m);
  const next = rules.apply(pos, m);
  let san: string;
  if (flags & FLAG_ACTION) {
    san = rules.variant.actionSan?.(pos, m) ?? moveToUci(m);
  } else if (flags & FLAG_DROP) {
    san = `${pieceDef(moveFrom(m)).letter.toUpperCase()}@${squareName(to)}`;
  } else {
    const from = moveFrom(m);
    const piece = pos.board[from];
    const def = pieceDef(piece & TYPE_MASK);
    if (flags & FLAG_CASTLE) {
      san = to > from ? 'O-O' : 'O-O-O';
    } else if (def.pawn && !isNeutral(piece)) {
      san = flags & FLAG_CAPTURE ? `${'abcdefgh'[from & 7]}x` : '';
      san += squareName(to);
      const promo = promotedTo(pos, next, m);
      if (promo) san += `=${pieceDef(promo).letter.toUpperCase()}`;
    } else {
      // Pieces written with the same letter (a knight and a wounded knight) need telling apart.
      const kind = baseType(piece & TYPE_MASK);
      const rivals = legal.filter((o) => {
        if (o === m || moveFlags(o) & FLAG_DROP || moveTo(o) !== to) return false;
        const q = pos.board[moveFrom(o)];
        return q === piece || ((q & COLOR_MASK) === (piece & COLOR_MASK) && baseType(q & TYPE_MASK) === kind);
      });
      let dis = '';
      if (rivals.length) {
        const sameFile = rivals.some((o) => (moveFrom(o) & 7) === (from & 7));
        const sameRank = rivals.some((o) => moveFrom(o) >> 3 === from >> 3);
        if (!sameFile) dis = 'abcdefgh'[from & 7];
        else if (!sameRank) dis = String((from >> 3) + 1);
        else dis = squareName(from);
      }
      san = pieceDef(kind).letter.toUpperCase() + dis + (flags & FLAG_CAPTURE ? 'x' : '') + squareName(to);
      const promo = movePromo(m);
      if (promo) san += `=${pieceDef(promo).letter.toUpperCase()}`;
    }
  }
  if (next && next.turn !== pos.turn) {
    const status = rules.terminal(next) ?? (rules.hasLegalMove(next) ? null : rules.noMovesStatus(next));
    if (status?.over && status.winner === pos.turn) san += '#';
    // A "+" would give a hidden king away.
    else if (!rules.variant.hiddenInfo && rules.inCheck(next)) san += '+';
  }
  return san;
}

/**
 * What a pawn move actually promoted to, which variants can change: a pawn that only wounds
 * stays put (no promotion), one reaching the last row left standing becomes a queen.
 */
function promotedTo(pos: Position, next: Position | null, m: number): number {
  if (!next) return movePromo(m);
  const from = moveFrom(m);
  const pawn = pos.board[from];
  if (next.board[from] === pawn) return 0;
  const landed = next.board[moveTo(m)];
  if ((landed & COLOR_MASK) === (pawn & COLOR_MASK) && !pieceDef(landed & TYPE_MASK).pawn) return landed & TYPE_MASK;
  return movePromo(m);
}

/** Finds the legal move matching a SAN string (check/mate markers optional). */
export function sanToMove(rules: Rules, pos: Position, san: string, legal = rules.legalMoves(pos)): number | null {
  const clean = (s: string) => s.replace(/[+#!?]/g, '').replace(/0/g, 'O');
  const want = clean(san);
  for (const m of legal) if (clean(moveToSan(rules, pos, m, legal)) === want) return m;
  return null;
}

export function pieceName(type: number): string {
  return PIECE_DEFS[type]?.name ?? 'piece';
}
